<script setup>
import { props as formGroupProps, useFormGroup } from '@phoenix/composables/components/form/formGroup';
import { computed } from 'vue';

const props = defineProps({
  ...formGroupProps,
  isFieldset: {
    type: Boolean,
    default: false,
  },
  hasFieldsetFields: {
    type: Boolean,
    default: true,
  },
  fieldsetLegendClass: {
    type: String,
    default: '',
  },
});

const {
  _shouldDisplayCharLeft,
  charLeftClasses,
  charactersLeft,
  displayedErrors,
  errorId,
  field,
  fieldUniqueId,
  hasLegendSlot,
  helpId,
  isHidden,
  isInvalid,
  labelId,
} = useFormGroup(props);

const fieldsetLegendClasses = computed(() => [
  'fieldset__legend',
  props.fieldsetLegendClass,
  { 'fieldset__legend--error': isInvalid },
]);
</script>

<template>
  <div v-if="!isFieldset" :key="`form-group-${fieldUniqueId}`" class="form-field">
    <label
      v-if="field.label && !isHidden && showLabel"
      :id="labelId"
      :for="fieldUniqueId"
      class="input-label form-field__label"
      :class="{ 'input-label--error': isInvalid, 'input-label--disabled': field.disabled }"
    >
      <span class="input-label__text">
        {{ field.label }}
        <abbr v-if="field.required" :title="$t('Form.required')" data-cy="form-input-required">*</abbr>
      </span>
    </label>

    <slot />

    <div v-if="field.help" :id="helpId" class="form-helper-text form-field__helper-text" v-html="field.help" />
    <slot v-else name="help" />

    <div v-if="_shouldDisplayCharLeft" :class="charLeftClasses">
      {{ $t('Form.charactersLeft', { charactersLeft }) }}
    </div>

    <div v-if="isInvalid" :id="errorId" class="form-helper-text form-helper-text--error form-field__helper-text">
      {{ displayedErrors }}
    </div>
  </div>

  <fieldset v-else class="fieldset">
    <legend v-if="hasLegendSlot || (field.label && !isHidden && showLabel)" :class="fieldsetLegendClasses">
      <slot name="legend">
        {{ field.label }}
        <abbr v-if="field.required" :title="$t('Form.required')" data-cy="form-input-required">*</abbr>
      </slot>
    </legend>

    <div v-if="hasFieldsetFields" class="fieldset__fields">
      <slot />
    </div>

    <slot v-else />
  </fieldset>
</template>
